// Parser for a constrained subset of Mermaid syntax (flowchart / sequenceDiagram /
// classDiagram). We deliberately do NOT depend on the mermaid library — the LLM emits
// familiar Mermaid-ish syntax (which it generates reliably), and we render it ourselves
// with the cosmic animated-dots engine. Anything outside the supported subset returns null
// so the caller can fall back to showing the raw code block.

export type Dir = 'LR' | 'RL' | 'TD' | 'TB'
export type NodeShape = 'rect' | 'round' | 'stadium' | 'cylinder' | 'diamond' | 'circle'

export interface FlowNode {
  id: string
  label: string
  shape: NodeShape
}
export interface FlowEdge {
  from: string
  to: string
  label?: string
  dashed?: boolean
}
export interface FlowSpec {
  kind: 'flow'
  dir: Dir
  nodes: FlowNode[]
  edges: FlowEdge[]
}

export interface SeqMessage {
  from: string
  to: string
  text: string
  dashed: boolean
}
export interface SeqSpec {
  kind: 'seq'
  actors: string[]
  messages: SeqMessage[]
}

export type RelKind = 'assoc' | 'inherit' | 'dep'
export interface ClassRel {
  from: string
  to: string
  label?: string
  kind: RelKind
}
export interface ClassBox {
  name: string
  members: string[]
}
export interface ClassSpec {
  kind: 'class'
  classes: ClassBox[]
  relations: ClassRel[]
}

export type Diagram = FlowSpec | SeqSpec | ClassSpec

const cleanLines = (code: string): string[] =>
  code
    .split(/\r?\n/)
    .map((l) => l.replace(/%%.*$/, '').trim()) // strip %% comments
    .filter((l) => l.length > 0)

// parseDiagram detects the diagram kind from the header keyword and dispatches. Returns null
// for anything we can't render, so the Markdown renderer falls back to a styled code block.
export function parseDiagram(code: string): Diagram | null {
  const lines = cleanLines(code)
  if (lines.length === 0) return null
  const head = lines[0].toLowerCase()
  if (head.startsWith('sequencediagram')) return parseSeq(lines.slice(1))
  if (head.startsWith('classdiagram')) return parseClass(lines.slice(1))
  if (head.startsWith('statediagram')) return parseState(lines.slice(1))
  if (head.startsWith('flowchart') || head.startsWith('graph')) {
    const m = lines[0].match(/^(?:flowchart|graph)\s+(LR|RL|TD|TB)/i)
    return parseFlow(lines.slice(1), (m ? (m[1].toUpperCase() as Dir) : 'LR'))
  }
  return null
}

// ---- flowchart ------------------------------------------------------------------------

// A node token is an id plus an optional shape wrapper, e.g. API[REST API], DB[(Store)],
// Q{Decision}. Longer delimiters are matched first so [( )] beats [ ].
function parseNodeToken(tok: string): FlowNode | null {
  const t = tok.trim()
  const shapes: Array<[RegExp, NodeShape]> = [
    [/^([\w-]+)\(\((.+)\)\)$/, 'circle'],
    [/^([\w-]+)\[\((.+)\)\]$/, 'cylinder'],
    [/^([\w-]+)\(\[(.+)\]\)$/, 'stadium'],
    [/^([\w-]+)\{(.+)\}$/, 'diamond'],
    [/^([\w-]+)\[(.+)\]$/, 'rect'],
    [/^([\w-]+)\((.+)\)$/, 'round'],
  ]
  for (const [re, shape] of shapes) {
    const m = t.match(re)
    if (m) return { id: m[1], label: cleanLabel(m[2]), shape }
  }
  if (/^[\w-]+$/.test(t)) return { id: t, label: t, shape: 'rect' }
  return null
}

// cleanLabel strips wrapping quotes and folds the line-break forms the LLM emits inside
// labels (literal "\n", <br>, <br/>) down to a single space so they fit one box.
const cleanLabel = (s: string): string =>
  s
    .trim()
    .replace(/^["']|["']$/g, '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
const stripQuotes = (s: string): string => s.trim().replace(/^["']|["']$/g, '').trim()

function parseFlow(lines: string[], dir: Dir): FlowSpec | null {
  const nodes = new Map<string, FlowNode>()
  const edges: FlowEdge[] = []
  const addNode = (n: FlowNode | null) => {
    if (!n) return
    const prev = nodes.get(n.id)
    // Keep the richest label/shape seen for an id (a later [Label] wins over a bare id).
    if (!prev || (prev.label === prev.id && n.label !== n.id)) nodes.set(n.id, n)
  }
  const edgeRe = /^(.+?)\s*(-->|---|-\.->|==>)\s*(?:\|([^|]*)\|\s*)?(.+)$/
  for (const ln of lines) {
    // Flatten subgraphs: render their contained nodes/edges, drop the grouping wrapper.
    if (/^(subgraph\b|end$|direction\s)/i.test(ln.trim())) continue
    const m = ln.match(edgeRe)
    if (m) {
      const from = parseNodeToken(m[1])
      const to = parseNodeToken(m[4])
      if (from && to) {
        addNode(from)
        addNode(to)
        edges.push({ from: from.id, to: to.id, label: m[3] ? stripQuotes(m[3]) : undefined, dashed: m[2].includes('.') })
      }
      continue
    }
    addNode(parseNodeToken(ln)) // standalone node declaration
  }
  if (nodes.size === 0) return null
  return { kind: 'flow', dir, nodes: [...nodes.values()], edges }
}

// A state diagram renders with the flow engine: states are nodes, transitions are edges
// (`A --> B : event`), and `[*]` start/end markers become small terminal nodes. Composite
// `state X { ... }` wrappers and notes are flattened (only their transitions matter here).
function parseState(lines: string[]): FlowSpec | null {
  const nodes = new Map<string, FlowNode>()
  const edges: FlowEdge[] = []
  const node = (id: string, label: string, shape: NodeShape): string => {
    if (!nodes.has(id)) nodes.set(id, { id, label, shape })
    return id
  }
  const ref = (raw: string, asTarget: boolean): string =>
    raw === '[*]' ? node(asTarget ? '_end' : '_start', '●', 'circle') : node(raw, raw, 'rect')
  const edgeRe = /^(\[\*\]|\w[\w-]*)\s*-->\s*(\[\*\]|\w[\w-]*)\s*(?::\s*(.+))?$/
  for (const ln of lines) {
    const m = ln.trim().match(edgeRe)
    if (m) edges.push({ from: ref(m[1], false), to: ref(m[2], true), label: m[3] ? cleanLabel(m[3]) : undefined })
  }
  if (nodes.size === 0) return null
  return { kind: 'flow', dir: 'TD', nodes: [...nodes.values()], edges }
}

// ---- sequence -------------------------------------------------------------------------

function parseSeq(lines: string[]): SeqSpec | null {
  const actors: string[] = []
  const alias = new Map<string, string>()
  const messages: SeqMessage[] = []
  const ensure = (id: string) => {
    if (!actors.includes(id)) actors.push(id)
  }
  // ID = word chars with internal hyphens only (no trailing '-', so it can't swallow the
  // arrow's leading dash, e.g. EX-->>API must yield actor "EX", not "EX-").
  const msgRe = /^(\w+(?:-\w+)*)\s*(--?>>?|--?x)\s*(\w+(?:-\w+)*)\s*:\s*(.+)$/
  for (const ln of lines) {
    const p = ln.match(/^(?:participant|actor)\s+(\w+(?:-\w+)*)(?:\s+as\s+(.+))?$/i)
    if (p) {
      ensure(p[1])
      if (p[2]) alias.set(p[1], stripQuotes(p[2]))
      continue
    }
    const m = ln.match(msgRe)
    if (m) {
      ensure(m[1])
      ensure(m[3])
      messages.push({ from: m[1], to: m[3], text: m[4].trim(), dashed: m[2].startsWith('--') })
    }
    // Unsupported constructs (Note/loop/alt/opt) are skipped silently.
  }
  if (actors.length === 0 || messages.length === 0) return null
  return { kind: 'seq', actors: actors.map((a) => alias.get(a) ?? a), messages: messages.map((m) => ({ ...m, from: alias.get(m.from) ?? m.from, to: alias.get(m.to) ?? m.to })) }
}

// ---- class ----------------------------------------------------------------------------

function parseClass(lines: string[]): ClassSpec | null {
  const classes = new Map<string, ClassBox>()
  const relations: ClassRel[] = []
  const ensure = (name: string): ClassBox => {
    let c = classes.get(name)
    if (!c) {
      c = { name, members: [] }
      classes.set(name, c)
    }
    return c
  }
  let open: ClassBox | null = null
  // Relation arrows: inheritance (--|>, <|--) vs association/dependency (everything else).
  const relRe = /^(\w+(?:-\w+)*)\s*(?:"[^"]*"\s*)?(<\|--|<\|\.\.|--\|>|\.\.\|>|\*--|o--|-->|\.\.>|--)\s*(?:"[^"]*"\s*)?(\w+(?:-\w+)*)\s*(?::\s*(.+))?$/
  for (const ln of lines) {
    if (open) {
      if (ln === '}' || ln.endsWith('}')) {
        const inner = ln.replace(/}$/, '').trim()
        if (inner) open.members.push(inner)
        open = null
        continue
      }
      open.members.push(ln)
      continue
    }
    const cm = ln.match(/^class\s+([\w-]+)\s*(\{)?$/)
    if (cm) {
      const c = ensure(cm[1])
      if (cm[2]) open = c
      continue
    }
    const r = ln.match(relRe)
    if (r) {
      ensure(r[1])
      ensure(r[3])
      const arrow = r[2]
      const kind: RelKind = arrow.includes('|') ? 'inherit' : arrow.startsWith('..') ? 'dep' : 'assoc'
      // Inheritance/realization points child→parent; normalize <|-- and <|.. to child→parent.
      const flip = arrow === '<|--' || arrow === '<|..'
      relations.push({ from: flip ? r[3] : r[1], to: flip ? r[1] : r[3], label: r[4] ? stripQuotes(r[4]) : undefined, kind })
    }
  }
  if (classes.size === 0) return null
  return { kind: 'class', classes: [...classes.values()], relations }
}
